import { useEffect, useRef, useState } from "react";
import { IconEdit, IconMapPin } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { useSaleDetails } from "../../../sales/hooks/useSaleDetails";
import { useCheckoutStore } from "../../domain/useCheckoutStore";
import { useUpdateSaleShippingAddress } from "../../hooks/useUpdateSaleShippingAddress";
import { DeliveryAddressSection } from "./delivery-address-section";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { getSaleDeliveryAddressId } from "../../utils/sale-delivery-address";

interface Props {
  saleId: number;
  /**
   * Open the picker straight away even though the order has an address —
   * the BE refused it for payment (archived, incomplete, not a delivery
   * address), so the buyer has to choose another.
   */
  startEditing?: boolean;
  /** Called after a new address was saved onto the order. */
  onSaved?: () => void;
}

/**
 * Shown above the payment form when paying an existing quotation
 * (`/checkout?saleId=…`). The buyer sees the current delivery address from
 * the sale and can swap it for any saved delivery address (or add a new
 * one) — provided the order isn't confirmed yet.
 *
 * Address change goes via `PATCH /sales/:id/shipping-address`. The BE
 * rejects the update once the order is confirmed (Odoo stock pickings
 * have already snapshotted the address); we surface that error inline.
 */
export function SaleShippingAddressSection({ saleId, startEditing = false, onSaved }: Props) {
  const { data: sale, isLoading } = useSaleDetails(saleId);
  const { update, isLoading: isUpdating } = useUpdateSaleShippingAddress();
  const { shippingAddressId, setShippingAddressId } = useCheckoutStore();
  const currentAddressId = getSaleDeliveryAddressId(sale);
  const hasDeliveryAddress = currentAddressId != null;
  const [isEditing, setIsEditing] = useState(false);

  const ship = hasDeliveryAddress ? sale?.shippingPartner : undefined;

  // Mirror the order's delivery address into the checkout store once the
  // sale loads, so the picker starts on it. Only once: the picker clears
  // the selection when that address is no longer in the book, and seeding
  // again would fight it. Without an address on the order, open the picker.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!sale || seededRef.current) return;
    seededRef.current = true;
    setShippingAddressId(currentAddressId);
    if (!hasDeliveryAddress || startEditing) setIsEditing(true);
  }, [sale, currentAddressId, hasDeliveryAddress, startEditing, setShippingAddressId]);

  if (isLoading || !sale) return null;

  const formatted = ship
    ? [
        ship.street,
        ship.street2,
        [ship.zip, ship.city].filter(Boolean).join(" "),
        ship.country?.name,
      ]
        .filter(Boolean)
        .join(", ")
    : "No delivery address on this order yet";

  const handleSave = async () => {
    if (shippingAddressId == null) {
      showErrorToast("Please select an address first.");
      return;
    }
    if (shippingAddressId === currentAddressId) {
      setIsEditing(false);
      return;
    }
    try {
      await update(saleId, shippingAddressId);
      showSuccessToast("Delivery address updated");
      setIsEditing(false);
      onSaved?.();
    } catch (err: any) {
      showErrorToast(
        err?.response?.data?.message ??
          err?.message ??
          "Could not update delivery address.",
      );
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <IconMapPin size={20} className="mt-0.5 text-textDark" />
          <div className="flex flex-col gap-0.5">
            <Heading variant="h3" tone="dark">Delivery to</Heading>
            {ship && <Text variant="bodyBold" tone="dark">{ship.name ?? "—"}</Text>}
            <Text variant="bodySm" tone="muted">{formatted || "—"}</Text>
            {ship?.phone && (
              <Text variant="bodyXs" tone="muted">{ship.phone}</Text>
            )}
          </div>
        </div>
        {!isEditing && hasDeliveryAddress && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<IconEdit size={16} />}
            onClick={() => setIsEditing(true)}
          >
            Change
          </Button>
        )}
      </div>

      {isEditing && (
        <div className="flex flex-col gap-4 border-t border-borderDefault pt-4">
          <DeliveryAddressSection />
          <div className="flex items-center justify-end gap-3">
            {hasDeliveryAddress && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  // Revert the in-memory selection to whatever's actually on
                  // the order so closing without saving doesn't silently
                  // override the picker on the next render.
                  setShippingAddressId(currentAddressId);
                  setIsEditing(false);
                }}
                disabled={isUpdating}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={isUpdating || shippingAddressId == null}
            >
              {isUpdating ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
